import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";

const Privacy = () => {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between max-w-4xl">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[hsl(152,40%,53%)]" />
            <h1 className="text-base font-semibold">Política de Privacidad</h1>
          </div>
          <div className="w-20" />
        </div>
      </header>

      <main className="container mx-auto px-6 py-10 max-w-3xl prose prose-sm prose-slate">
        <article className="space-y-8 text-sm leading-relaxed text-foreground">
          <header className="space-y-2 not-prose">
            <h2 className="text-2xl font-bold">Política de Privacidad y Protección de Datos</h2>
            <p className="text-muted-foreground text-xs">Última actualización: {today}</p>
          </header>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">1. Responsable del tratamiento</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Razón social:</strong> Ferreiro & Portelo, Asesores</li>
              <li><strong>Domicilio:</strong> [Dirección postal de la asesoría]</li>
              <li><strong>NIF:</strong> [NIF de la asesoría]</li>
              <li><strong>Correo de contacto:</strong> silvia.sanchez@ferreiroyportelo.es</li>
              <li><strong>Sitio web:</strong> gestion.ferreiroyportelo.es</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">2. Finalidad del tratamiento</h3>
            <p>
              Tratamos los datos personales y fiscales que nos facilita el usuario o que se obtienen
              de los documentos contables aportados (facturas, contratos de leasing, datos de
              proveedores) con las siguientes finalidades:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Prestación de servicios de asesoría contable, fiscal y administrativa.</li>
              <li>Cumplimiento de obligaciones legales tributarias y mercantiles.</li>
              <li>Gestión de la relación contractual y comunicaciones con clientes.</li>
              <li>Conservación de registros contables conforme a la Ley General Tributaria.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">3. Base legal del tratamiento</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Ejecución del contrato</strong> de prestación de servicios (art. 6.1.b RGPD).</li>
              <li><strong>Cumplimiento de obligación legal</strong> (art. 6.1.c RGPD): Ley 58/2003 General Tributaria, Código de Comercio, Ley 11/2021 antifraude.</li>
              <li><strong>Interés legítimo</strong> en la seguridad de los sistemas y prevención del fraude (art. 6.1.f RGPD).</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">4. Categorías de datos tratados</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Datos identificativos: nombre, NIF/CIF, dirección, correo electrónico.</li>
              <li>Datos económico-fiscales: facturas, importes, cuentas contables, datos bancarios.</li>
              <li>Datos de acceso: correo, contraseña cifrada, registro de actividad en la aplicación.</li>
            </ul>
            <p className="text-muted-foreground">No se tratan categorías especiales de datos (salud, ideología, etc.).</p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">5. Plazo de conservación</h3>
            <p>
              Los datos contables y fiscales se conservan durante un mínimo de <strong>6 años</strong>,
              conforme al artículo 30 del Código de Comercio y artículo 66 de la Ley General Tributaria.
              Los datos de acceso a la aplicación se conservan mientras el usuario mantenga su cuenta
              activa y hasta 2 años después de su cancelación a efectos de auditoría.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">6. Destinatarios y encargados del tratamiento</h3>
            <p>
              Sus datos pueden ser comunicados a las siguientes entidades, exclusivamente para las
              finalidades descritas:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Agencia Estatal de Administración Tributaria (AEAT)</strong> y otras administraciones públicas, por obligación legal.</li>
              <li><strong>Lovable Technologies AB</strong> (Suecia, UE) – proveedor de la plataforma.</li>
              <li><strong>Supabase / AWS</strong> (servidores en la Unión Europea) – almacenamiento y base de datos.</li>
              <li><strong>Google Cloud (Lovable AI Gateway)</strong> – procesamiento puntual de imágenes de facturas mediante IA, sin retención de datos.</li>
            </ul>
            <p>
              Todos los encargados están vinculados mediante contrato de encargado del tratamiento
              conforme al artículo 28 del RGPD y procesan los datos dentro del Espacio Económico Europeo
              o bajo cláusulas contractuales tipo aprobadas por la Comisión Europea.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">7. Transferencias internacionales</h3>
            <p>
              No se realizan transferencias de datos fuera del EEE. Toda la infraestructura técnica
              utilizada está alojada en servidores ubicados en la Unión Europea.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">8. Derechos del interesado</h3>
            <p>Puede ejercer en cualquier momento los siguientes derechos:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Acceso</strong> a sus datos personales.</li>
              <li><strong>Rectificación</strong> de datos inexactos.</li>
              <li><strong>Supresión</strong> ("derecho al olvido"), salvo obligación legal de conservación.</li>
              <li><strong>Limitación</strong> y <strong>oposición</strong> al tratamiento.</li>
              <li><strong>Portabilidad</strong> de sus datos en formato estructurado.</li>
              <li>No ser objeto de decisiones automatizadas con efectos jurídicos.</li>
            </ul>
            <p>
              Para ejercer estos derechos, envíe una solicitud a{" "}
              <a href="mailto:silvia.sanchez@ferreiroyportelo.es" className="text-[hsl(152,40%,40%)] underline">
                silvia.sanchez@ferreiroyportelo.es
              </a>{" "}
              acompañando copia del DNI. Si considera que sus derechos no han sido atendidos, puede
              presentar reclamación ante la <strong>Agencia Española de Protección de Datos</strong>{" "}
              (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[hsl(152,40%,40%)] underline">www.aepd.es</a>).
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">9. Medidas de seguridad</h3>
            <p>Aplicamos medidas técnicas y organizativas conforme al artículo 32 del RGPD:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Cifrado en tránsito (HTTPS/TLS) y en reposo.</li>
              <li>Control de accesos por roles con autenticación reforzada y políticas de seguridad de fila.</li>
              <li>Validación de contraseñas frente a bases de datos de credenciales filtradas (HIBP).</li>
              <li>Registro de auditoría inmutable de todas las operaciones sobre datos sensibles.</li>
              <li>Aislamiento estricto de datos entre empresas clientes.</li>
              <li>Copias de seguridad automáticas diarias.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">10. Cookies</h3>
            <p>
              Esta aplicación utiliza únicamente cookies técnicas estrictamente necesarias para el
              funcionamiento de la sesión de usuario (autenticación). No se utilizan cookies de
              análisis, publicidad ni de terceros con fines de seguimiento.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">11. Modificaciones</h3>
            <p>
              Nos reservamos el derecho a modificar esta política para adaptarla a novedades
              legislativas o de operativa. La fecha de la última actualización aparece al inicio de
              este documento.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-lg font-semibold">12. Notificación de brechas de seguridad</h3>
            <p>
              En caso de incidente de seguridad que afecte a datos personales, notificaremos a la
              Agencia Española de Protección de Datos en un plazo máximo de 72 horas y, cuando
              corresponda, a los interesados afectados, conforme a los artículos 33 y 34 del RGPD.
            </p>
          </section>
        </article>
      </main>

      <footer className="border-t mt-10 py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Ferreiro & Portelo · <button onClick={() => navigate("/")} className="hover:text-foreground transition-colors">Inicio</button>
      </footer>
    </div>
  );
};

export default Privacy;
